(function () {
  const VARIANT_NAME = 'probe 2ml';

  function money(cents) {
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
  }

  function isProbeVariant(variant) {
    const values = [variant.title, variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    return values.some((value) => value.includes(VARIANT_NAME));
  }

  function probeVariantName(variant) {
    const match = [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .find((value) => String(value).toLowerCase().includes(VARIANT_NAME));
    if (match) return match;
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return '2ml Probe';
  }

  function resolveProductImage(product, probeVariant) {
    const images = product.images || [];
    const probeImageId = probeVariant?.featured_image?.id;
    const probeVariantId = probeVariant?.id;
    const imageSrc = (image) => image?.src || image || '';

    const fromOtherVariant = images.find((image) => {
      const variantIds = image.variant_ids || [];
      return variantIds.length > 0 && !variantIds.includes(probeVariantId);
    });
    if (fromOtherVariant) return imageSrc(fromOtherVariant);

    const notProbeImage = images.find((image) => {
      if (probeImageId && image.id === probeImageId) return false;
      const variantIds = image.variant_ids || [];
      return !variantIds.includes(probeVariantId);
    });
    if (notProbeImage) return imageSrc(notProbeImage);

    return imageSrc(product.featured_image);
  }

  function priceToCents(value) {
    if (typeof value === 'number') return Math.round(value < 1000 ? value * 100 : value);
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (Number.isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
  }

  class ProbeBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.config = this.readConfig();
      this.products = [];
      this.selectedIds = [];
      this.grid = this.querySelector('[data-probe-grid]');
      this.slots = this.querySelectorAll('[data-probe-slot]');
      this.panel = this.querySelector('[data-probe-panel]');
      this.searchInput = this.querySelector('[data-probe-search]');
      this.vendorSelect = this.querySelector('[data-probe-vendor]');
      this.emptyEl = this.querySelector('[data-probe-empty]');
      this.atcButton = this.querySelector('[data-action="add-to-cart"]');
      this.minProducts = parseInt(this.dataset.minProducts || this.config.minProducts || 5, 10);
      this.maxProducts = parseInt(this.dataset.maxProducts || this.config.maxProducts || 5, 10);
      this.bindEvents();
      this.loadProducts().then(() => {
        this.renderCards();
        this.updateUI();
      });
    }

    readConfig() {
      const script = this.querySelector('[data-probe-config]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Probe builder config failed', error);
        return {};
      }
    }

    async loadProducts() {
      const seeded = this.config.products || [];
      if (seeded.length) {
        this.products = seeded.map((product) => ({ ...product, available: true }));
      }

      const handle = this.config.collectionHandle || 'duftproben-2ml';
      const root = window.Shopify?.routes?.root || '/';
      const loaded = [];

      try {
        for (let page = 1; page <= 8; page += 1) {
          const response = await fetch(`${root}collections/${handle}/products.json?limit=250&page=${page}`);
          if (!response.ok) break;
          const data = await response.json();
          const products = data.products || [];
          if (!products.length) break;
          products.forEach((product) => {
            const variant = (product.variants || []).find((item) => isProbeVariant(item));
            if (!variant) return;
            loaded.push({
              id: product.id,
              variantId: variant.id,
              title: product.title,
              variantName: probeVariantName(variant),
              vendor: product.vendor || '',
              price: priceToCents(variant.price),
              image: resolveProductImage(product, variant),
              available: true,
              handle: product.handle
            });
          });
          if (products.length < 250) break;
        }
      } catch (error) {
        console.error('Probe builder products failed', error);
      }

      if (loaded.length) this.products = loaded;
      if (!this.products.length) this.toggleEmpty(true);
      this.populateVendors();
    }

    populateVendors() {
      if (!this.vendorSelect) return;
      const vendors = [...new Set(this.products.map((product) => product.vendor).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));
      vendors.forEach((vendor) => {
        const option = document.createElement('option');
        option.value = vendor;
        option.textContent = vendor;
        this.vendorSelect.appendChild(option);
      });
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const addBtn = event.target.closest('[data-action="add-product"]');
        if (addBtn) {
          const card = addBtn.closest('[data-variant-id]');
          if (card) this.addVariant(card.dataset.variantId);
          return;
        }

        const card = event.target.closest('.bk-media-probe-builder__card.is-addable');
        if (card && !event.target.closest('button, input, quantity-selector')) {
          this.addVariant(card.dataset.variantId);
          return;
        }

        const removeSlot = event.target.closest('[data-action="remove-slot"]');
        if (removeSlot) {
          const slot = removeSlot.closest('[data-slot-index]');
          const index = parseInt(slot?.dataset.slotIndex || '-1', 10);
          if (index >= 0) this.removeAt(index);
          return;
        }

        const toggle = event.target.closest('[data-action="toggle-panel"]');
        if (toggle && this.panel) {
          const expanded = this.panel.getAttribute('data-expanded') === 'true';
          this.panel.setAttribute('data-expanded', expanded ? 'false' : 'true');
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          return;
        }

        if (event.target.closest('[data-action="add-to-cart"]')) {
          this.addToCart();
          return;
        }

        const minus = event.target.closest('[data-action="qty-minus"]');
        if (minus) {
          const card = event.target.closest('[data-variant-id]');
          if (!card) return;
          const index = this.selectedIds.lastIndexOf(card.dataset.variantId);
          if (index >= 0) this.removeAt(index);
        }
      });

      this.searchInput?.addEventListener('input', () => this.filterCards());
      this.vendorSelect?.addEventListener('change', () => this.filterCards());
    }

    addVariant(variantId) {
      if (!variantId || this.selectedIds.length >= this.maxProducts) return;
      if (this.selectedIds.includes(String(variantId))) return;
      const product = this.products.find((item) => String(item.variantId) === String(variantId));
      if (!product) return;
      const alreadySameProduct = this.selectedIds.some((id) => {
        const selected = this.products.find((item) => String(item.variantId) === String(id));
        return selected && String(selected.id) === String(product.id);
      });
      if (alreadySameProduct) return;
      this.selectedIds.push(String(variantId));
      this.updateUI();
    }

    removeAt(index) {
      this.selectedIds.splice(index, 1);
      this.updateUI();
    }

    getDiscount(count) {
      return (this.config.goals || []).find((goal) => goal.quantity === count) || null;
    }

    bundleCents(discount) {
      const value = Number(discount?.bundlePrice || 0);
      if (value <= 0) return 0;
      return Math.round(value);
    }

    totals() {
      const count = this.selectedIds.length;
      const subtotal = this.selectedIds.reduce((sum, id) => {
        const product = this.products.find((item) => String(item.variantId) === String(id));
        return sum + (product?.price || 0);
      }, 0);
      const discount = this.getDiscount(count);
      const bundlePrice = this.bundleCents(discount);
      if (bundlePrice > 0 && discount?.quantity === count) {
        const unitPrice = Math.round(bundlePrice / count);
        const sale = bundlePrice;
        const savings = Math.max(0, subtotal - sale);
        const percent = subtotal > 0 ? Math.round(savings / subtotal * 100) : 0;
        return { subtotal, sale, percent, savings, unitPrice, bundle: true };
      }
      return { subtotal, sale: subtotal, percent: 0, savings: 0, unitPrice: 0, bundle: false };
    }

    renderCards() {
      if (!this.grid) return;
      this.grid.innerHTML = '';
      this.products.forEach((product) => {
        const card = document.createElement('article');
        card.className = 'bk-media-probe-builder__card';
        card.dataset.productId = String(product.id);
        card.dataset.variantId = String(product.variantId);
        card.dataset.vendor = product.vendor;
        card.dataset.title = product.title;
        card.dataset.variantName = product.variantName || '';
        card.dataset.available = String(product.available);
        card.innerHTML = `
          <div class="bk-media-probe-builder__card-media">
            ${product.image ? `<img class="bk-media-probe-builder__card-image" src="${product.image}" alt="${product.title.replace(/"/g, '&quot;')}" width="300" height="300" loading="lazy">` : ''}
            <span class="badge badge--on-sale bk-media-probe-builder__card-badge" data-card-discount-badge hidden></span>
          </div>
          <div class="bk-media-probe-builder__card-info">
            <p class="bk-media-probe-builder__card-vendor">${product.vendor}</p>
            <h6 class="bk-media-probe-builder__card-title h6">${product.title}</h6>
            <p class="bk-media-probe-builder__card-variant">${product.variantName || '2ml Probe'}</p>
          </div>
          <div class="bk-media-probe-builder__card-actions">
            <div class="bk-media-probe-builder__card-price h-stack gap-2 justify-center" data-card-price>${money(product.price)}</div>
            <button type="button" class="bk-media-probe-builder__card-add button" data-action="add-product" ${product.available ? '' : 'disabled'}>
              ${product.available ? (this.config.addButtonText || 'Hinzufügen') : (this.config.soldOutText || 'Ausverkauft')}
            </button>
            <p class="bk-media-probe-builder__card-added text-sm" data-probe-added hidden>Im Set</p>
          </div>
        `;
        this.grid.appendChild(card);
      });
      this.toggleEmpty(!this.products.length);
      this.filterCards();
    }

    filterCards() {
      const query = (this.searchInput?.value || '').trim().toLowerCase();
      const vendor = this.vendorSelect?.value || '';
      let visible = 0;
      this.querySelectorAll('.bk-media-probe-builder__card').forEach((card) => {
        const haystack = `${card.dataset.title || ''} ${card.dataset.vendor || ''} ${card.dataset.variantName || ''}`.toLowerCase();
        const matchQuery = !query || haystack.includes(query);
        const matchVendor = !vendor || card.dataset.vendor === vendor;
        const hidden = !(matchQuery && matchVendor);
        card.classList.toggle('is-hidden', hidden);
        if (!hidden) visible += 1;
      });
      this.toggleEmpty(visible === 0);
    }

    toggleEmpty(isEmpty) {
      if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
    }

    updateUI() {
      const count = this.selectedIds.length;
      const { subtotal, sale, savings, unitPrice, bundle } = this.totals();
      const atMax = count >= this.maxProducts;

      this.querySelectorAll('[data-probe-count]').forEach((el) => {
        el.textContent = `${count} / ${this.maxProducts}`;
      });

      this.querySelectorAll('[data-goal-quantity]').forEach((node) => {
        const quantity = parseInt(node.dataset.goalQuantity, 10);
        node.classList.toggle('is-reached', count >= quantity);
      });

      const fill = this.querySelector('[data-goals-fill]');
      const goals = this.config.goals || [];
      const maxGoal = goals.reduce((max, goal) => Math.max(max, goal.quantity || 0), 0);
      if (fill && maxGoal) fill.style.width = `${Math.min(100, count / maxGoal * 100)}%`;

      this.slots.forEach((slot, index) => {
        const variantId = this.selectedIds[index];
        const product = this.products.find((item) => String(item.variantId) === String(variantId));
        const titleEl = slot.querySelector('[data-slot-title]');
        const subtitleEl = slot.querySelector('[data-slot-subtitle]');
        const iconEl = slot.querySelector('[data-slot-icon]');
        const removeBtn = slot.querySelector('[data-action="remove-slot"]');
        slot.classList.toggle('is-filled', Boolean(product));
        if (titleEl) titleEl.textContent = product ? product.title : (this.config.emptySlotText || 'Wähle eine Probe');
        if (subtitleEl) {
          if (!product) {
            subtitleEl.textContent = '';
          } else {
            const slotPrice = bundle && unitPrice && unitPrice < product.price ? unitPrice : product.price;
            subtitleEl.textContent = `${product.variantName || '2ml Probe'} · ${product.vendor} · ${money(slotPrice)}`;
          }
        }
        if (iconEl) {
          iconEl.innerHTML = product?.image
            ? `<img src="${product.image}" alt="${product.title.replace(/"/g, '&quot;')}" width="48" height="48">`
            : '';
        }
        if (removeBtn) removeBtn.hidden = !product;
      });

      this.querySelectorAll('.bk-media-probe-builder__card').forEach((card) => {
        const variantId = card.dataset.variantId;
        const productCount = this.selectedIds.filter((id) => id === variantId).length;
        const addButton = card.querySelector('[data-action="add-product"]');
        const addedLabel = card.querySelector('[data-probe-added]');
        const priceEl = card.querySelector('[data-card-price]');
        const badge = card.querySelector('[data-card-discount-badge]');
        const product = this.products.find((item) => String(item.variantId) === variantId);
        card.classList.toggle('is-selected', productCount > 0);
        card.classList.toggle('is-addable', card.dataset.available === 'true' && !atMax && productCount === 0);
        if (priceEl && product) {
          priceEl.textContent = money(product.price);
        }
        if (badge) {
          badge.hidden = true;
          badge.textContent = '';
        }
        if (addButton) {
          addButton.hidden = productCount > 0;
          addButton.disabled = atMax || productCount > 0 || card.dataset.available !== 'true';
        }
        if (addedLabel) addedLabel.hidden = productCount === 0;
      });

      const savingsRow = this.querySelector('[data-probe-savings]');
      const savingsAmount = this.querySelector('[data-probe-savings-amount]');
      if (savingsRow) savingsRow.hidden = savings <= 0;
      if (savingsAmount) savingsAmount.textContent = money(savings);

      const atcPrices = this.querySelector('[data-atc-prices]');
      const atcSale = this.querySelector('[data-atc-sale-price]');
      const atcCompare = this.querySelector('[data-atc-compare-price]');
      if (atcPrices) atcPrices.hidden = count === 0;
      if (atcSale) atcSale.textContent = money(sale);
      if (atcCompare) {
        atcCompare.textContent = money(subtotal);
        atcCompare.hidden = savings <= 0;
      }

      if (this.atcButton) {
        this.atcButton.disabled = count < this.minProducts;
      }
    }

    premiumUnits() {
      return this.selectedIds.reduce((sum, id) => {
        const product = this.products.find((item) => String(item.variantId) === String(id));
        const price = product?.price || 0;
        if (price >= 1890) return sum + 2;
        if (price >= 1390) return sum + 1;
        return sum;
      }, 0);
    }

    discountNeededCents(units) {
      return [0, 450, 950, 1450, 1950, 2450, 2950, 3450, 3950, 4450, 4950][units] || 0;
    }

    leftoverSelected() {
      const leftoverIds = new Set((this.config.leftoverVariantIds || []).map(String));
      return this.selectedIds.filter((id) => leftoverIds.has(String(id)));
    }

    discountCode() {
      if (this.selectedIds.length !== 5) return '';
      const units = this.premiumUnits();
      if (units <= 0) return '';
      const mixCodes = this.config.mixCodes || {};
      const restCodes = this.config.mixRestCodes || {};
      const leftoverIds = this.leftoverSelected();
      const leftoverSubtotal = leftoverIds.reduce((sum, id) => {
        const product = this.products.find((item) => String(item.variantId) === String(id));
        return sum + (product?.price || 0);
      }, 0);
      const needed = this.discountNeededCents(units);
      const mainEligible = this.selectedIds.length - leftoverIds.length;
      if (leftoverIds.length && mainEligible < 5 && leftoverSubtotal >= needed) {
        return restCodes[String(units)] || mixCodes[String(units)] || '';
      }
      return mixCodes[String(units)] || '';
    }

    async loadCartSections(sectionIds, sectionsUrl) {
      const params = new URLSearchParams({ sections: sectionIds.join(',') });
      const url = `${sectionsUrl}${sectionsUrl.includes('?') ? '&' : '?'}${params}`;
      const response = await fetch(url);
      if (!response.ok) return {};
      return response.json();
    }

    async addToCart() {
      if (this.selectedIds.length < this.minProducts || !this.atcButton) return;
      this.atcButton.setAttribute('aria-busy', 'true');
      this.atcButton.disabled = true;

      try {
        const uniqueIds = [...new Set(this.selectedIds.map(String))];
        if (uniqueIds.length !== this.selectedIds.length) {
          throw new Error('Jeder Duft kann nur einmal als Probe ins Set.');
        }

        const existingCart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        const discountValues = [
          ...(existingCart.discount_codes || []).map((item) => String(item.code || '').toUpperCase()),
          ...(existingCart.cart_level_discount_applications || []).map((item) => String(item.title || '').toUpperCase()),
        ];
        const hasFollowupCredit = discountValues.some((value) => (
          value.startsWith('PROBE45') || value.includes('FOLLOW-UP') || value.includes('FOLLOW UP')
        ));
        const hasProbeSet = (existingCart.items || []).some((item) => item.properties && item.properties._ProbeSet === 'true');
        if (hasFollowupCredit) {
          throw new Error('Mit dem 45-€-Gutschein kannst du nur einen Full-Size-Duft bestellen, kein weiteres Proben-Set.');
        }
        if (hasProbeSet) {
          throw new Error('Du hast schon ein Proben-Set im Warenkorb.');
        }
        const guard = window.NischeProbenCart;
        const overlap = uniqueIds.find((id) => guard?.cartHasVariant(existingCart, id) || guard?.cartHasProduct(existingCart, this.products.find((item) => String(item.variantId) === String(id))?.id));
        if (overlap) {
          throw new Error(guard.messages.duplicateScent);
        }

        const quantities = {};
        uniqueIds.forEach((id) => {
          quantities[id] = 1;
        });

        const items = Object.entries(quantities).map(([id, quantity]) => ({
          id: parseInt(id, 10),
          quantity,
          properties: { _ProbeSet: 'true' }
        }));

        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const firstVariantId = items[0].id;
        const sectionsUrl = `${window.Shopify.routes.root}variants/${firstVariantId}`;
        const sectionIds = sectionsToBundle.join(',');

        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds,
            sections_url: sectionsUrl
          })
        });
        if (!addResponse.ok) {
          const error = await addResponse.json();
          throw new Error(error.description || 'Proben konnten nicht hinzugefügt werden.');
        }
        const addJson = await addResponse.json();
        let sectionHtml = addJson.sections || {};

        const code = this.discountCode();
        if (code) {
          const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              discount: code,
              sections: sectionIds,
              sections_url: sectionsUrl
            })
          });
          if (updateResponse.ok) {
            const updateJson = await updateResponse.json();
            if (updateJson.sections) sectionHtml = updateJson.sections;
          }
          const refreshed = await this.loadCartSections(sectionsToBundle, window.Shopify.routes.root);
          if (refreshed && Object.keys(refreshed).length) sectionHtml = refreshed;
        }

        if (!sectionHtml || !Object.keys(sectionHtml).length) {
          sectionHtml = await this.loadCartSections(sectionsToBundle, window.Shopify.routes.root);
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cart.sections = sectionHtml;

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: addJson.items || [addJson], cart }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));

        this.selectedIds = [];
        this.updateUI();
      } catch (error) {
        console.error(error);
        alert(error.message || 'Proben konnten nicht hinzugefügt werden.');
      } finally {
        this.atcButton.removeAttribute('aria-busy');
        this.atcButton.disabled = this.selectedIds.length < this.minProducts;
      }
    }
  }

  if (!customElements.get('bk-media-probe-builder')) {
    customElements.define('bk-media-probe-builder', ProbeBuilder);
  }
})();
